import { useEffect, useState } from "react";
import {
  LayoutGrid, Hash, GraduationCap, Calendar, Users, Plus, Trash2, Save,
  Shield, Ban, X, Clock, Check, ShieldAlert, Sparkles, AlertTriangle, Loader2,
  Archive, Download, RotateCcw, Flag,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Avatar } from "@/components/common";
import { ImageUpload } from "@/components/ImageUpload";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { toast } from "sonner";

const tabs = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "moderation", label: "Moderation", icon: ShieldAlert },
  { id: "spaces", label: "Spaces", icon: Hash },
  { id: "courses", label: "Courses", icon: GraduationCap },
  { id: "events", label: "Events", icon: Calendar },
  { id: "members", label: "Members", icon: Users },
  { id: "reports", label: "Reports", icon: Flag },
  { id: "danger", label: "Danger Zone", icon: AlertTriangle },
];

const input = "w-full bg-transparent border border-border px-3 py-2 text-sm outline-none focus:border-gold transition-colors";
const btn = "inline-flex items-center gap-1.5 bg-gold text-accent-foreground rounded-md px-4 py-2 text-sm hover:bg-gold-hover transition-colors";
const btnGhost = "inline-flex items-center gap-1.5 border border-border px-3 py-2 text-sm hover:bg-secondary/50 transition-colors";

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return null;
  const items = [
    ["Members", stats.members], ["Posts", stats.posts], ["Courses", stats.courses],
    ["Events", stats.events], ["Purchases", stats.purchases],
    ["Pending", stats.pending ?? 0], ["Projects", stats.projects ?? 0],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-border border border-border">
      {items.map(([label, val]) => (
        <div key={label} className="bg-card p-6" data-testid={`stat-${label.toLowerCase()}`}>
          <div className="font-serif text-4xl">{val}</div>
          <div className="eyebrow text-muted-foreground mt-2">{label}</div>
        </div>
      ))}
    </div>
  );
}

function Spaces() {
  const [spaces, setSpaces] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const load = () => api.get("/spaces/all").then((r) => setSpaces(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    await api.post("/admin/spaces", { name, description: desc });
    setName(""); setDesc(""); load(); toast.success("Space created");
  };
  const toggleHide = async (s) => { await api.put(`/admin/spaces/${s.space_id}`, { hidden: !s.hidden }); load(); };
  const togglePremium = async (s) => {
    await api.put(`/admin/spaces/${s.space_id}`, { premium: !s.premium });
    load(); toast.success(!s.premium ? `${s.name} is now Pro-only` : `${s.name} is open to all`);
  };
  const remove = async (s) => { await api.delete(`/admin/spaces/${s.space_id}`); load(); toast.success("Deleted"); };

  return (
    <div className="space-y-6">
      <div className="border border-border bg-card rounded-xl p-5 space-y-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Space name" className={input} data-testid="admin-space-name" />
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" className={input} data-testid="admin-space-desc" />
        <button onClick={create} className={btn} data-testid="admin-create-space"><Plus size={14} /> Create space</button>
      </div>
      <div className="border border-border bg-card rounded-xl divide-y divide-border">
        {spaces.map((s) => (
          <div key={s.space_id} className="flex items-center gap-3 p-4" data-testid={`admin-space-${s.slug}`}>
            <Hash size={15} className="text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium flex items-center gap-2 ${s.hidden ? "opacity-50 line-through" : ""}`}>
                {s.name}
                {s.premium && <span className="inline-flex items-center gap-1 text-[10px] bg-cobalt text-white rounded-full px-2 py-0.5"><Sparkles size={10} /> Pro</span>}
              </div>
              <div className="text-xs text-muted-foreground truncate">{s.description}</div>
            </div>
            <button onClick={() => togglePremium(s)} data-testid={`admin-space-premium-${s.slug}`}
              className={`text-xs rounded-md px-3 py-1.5 border transition-colors ${s.premium ? "border-cobalt text-cobalt" : "border-border hover:bg-secondary/60"}`}>
              {s.premium ? "Pro-only" : "Make Pro"}
            </button>
            <button onClick={() => toggleHide(s)} className={btnGhost}>{s.hidden ? "Show" : "Hide"}</button>
            <button onClick={() => remove(s)} className="p-2 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CourseEditor({ course, onClose, onSaved }) {
  const [c, setC] = useState(course);
  const set = (k, v) => setC({ ...c, [k]: v });
  const setLesson = (i, k, v) => {
    const lessons = [...c.lessons];
    lessons[i] = { ...lessons[i], [k]: v };
    setC({ ...c, lessons });
  };
  const addLesson = () => setC({ ...c, lessons: [...c.lessons, { lesson_id: `lesson_${Date.now()}`, title: "New lesson", content: "", video_url: null, order: c.lessons.length }] });
  const removeLesson = (i) => setC({ ...c, lessons: c.lessons.filter((_, idx) => idx !== i) });

  const save = async () => {
    await api.put(`/admin/courses/${c.course_id}`, {
      title: c.title, description: c.description, cover_image: c.cover_image,
      is_paid: c.is_paid, price: c.price, published: c.published,
      lessons: c.lessons.map((l, i) => ({ ...l, order: i })),
    });
    toast.success("Course saved"); onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="course-editor-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-2xl bg-background border-l border-border h-full overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl">Edit course</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <input value={c.title} onChange={(e) => set("title", e.target.value)} placeholder="Title" className={input} data-testid="course-edit-title" />
        <textarea value={c.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={3} className={input} />
        <div>
          <div className="eyebrow text-muted-foreground mb-2">Cover image</div>
          <ImageUpload value={c.cover_image || ""} onChange={(url) => set("cover_image", url)}
            aspect="aspect-[16/9]" label="Upload cover" testid="course-cover-upload" />
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.is_paid} onChange={(e) => set("is_paid", e.target.checked)} data-testid="course-edit-paid" /> Paid</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={c.published} onChange={(e) => set("published", e.target.checked)} /> Published</label>
          {c.is_paid && (
            <label className="flex items-center gap-2 text-sm">Price $
              <input type="number" value={c.price} onChange={(e) => set("price", e.target.value)} className="w-20 bg-transparent border border-border px-2 py-1" data-testid="course-edit-price" />
            </label>
          )}
        </div>
        {c.is_paid && (
          <p className="text-xs text-muted-foreground border border-border p-3">
            Note: new paid courses use the existing Stripe price (${course.price}). To sell at a different price, ask the builder to add a matching Stripe price.
          </p>
        )}
        <div className="gold-rule" />
        <div className="flex items-center justify-between">
          <div className="eyebrow text-gold">Lessons</div>
          <button onClick={addLesson} className={btnGhost}><Plus size={14} /> Add</button>
        </div>
        <div className="space-y-3">
          {c.lessons.map((l, i) => (
            <div key={l.lesson_id} className="border border-border p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input value={l.title} onChange={(e) => setLesson(i, "title", e.target.value)} placeholder="Lesson title" className={input} />
                <button onClick={() => removeLesson(i)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
              </div>
              <textarea value={l.content} onChange={(e) => setLesson(i, "content", e.target.value)} placeholder="Lesson content (Markdown supported)" rows={4} className={input} />
              <input value={l.video_url || ""} onChange={(e) => setLesson(i, "video_url", e.target.value)} placeholder="Video URL (optional)" className={input} />
            </div>
          ))}
        </div>
        <button onClick={save} className={btn} data-testid="course-editor-save"><Save size={14} /> Save course</button>
      </div>
    </div>
  );
}

function Courses() {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/courses").then((r) => setCourses(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const res = await api.post("/admin/courses", { title: "Untitled course", description: "", is_paid: false, published: false });
    const full = await api.get(`/courses/${res.data.course_id}`);
    setEditing(full.data); load();
  };
  const remove = async (c) => { await api.delete(`/admin/courses/${c.course_id}`); load(); toast.success("Deleted"); };
  const openEdit = async (c) => { const full = await api.get(`/courses/${c.course_id}`); setEditing(full.data); };

  return (
    <div className="space-y-6">
      <button onClick={create} className={btn} data-testid="admin-create-course"><Plus size={14} /> New course</button>
      <div className="border border-border bg-card rounded-xl divide-y divide-border">
        {courses.map((c) => (
          <div key={c.course_id} className="flex items-center gap-3 p-4" data-testid={`admin-course-${c.course_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{c.title}</div>
              <div className="text-xs text-muted-foreground">{c.is_paid ? `Paid · $${c.price}` : "Free"} · {c.lesson_count} lessons {!c.published && "· draft"}</div>
            </div>
            <button onClick={() => openEdit(c)} className={btnGhost} data-testid={`admin-edit-course-${c.course_id}`}>Edit</button>
            <button onClick={() => remove(c)} className="p-2 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      {editing && <CourseEditor course={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Events() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", starts_at: "", join_link: "" });
  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.starts_at) { toast.error("Title and date required"); return; }
    await api.post("/admin/events", { ...form, starts_at: new Date(form.starts_at).toISOString() });
    setForm({ title: "", description: "", starts_at: "", join_link: "" }); load(); toast.success("Event created");
  };
  const cancel = async (e) => { await api.put(`/admin/events/${e.event_id}`, { cancelled: !e.cancelled }); load(); };
  const remove = async (e) => { await api.delete(`/admin/events/${e.event_id}`); load(); toast.success("Deleted"); };

  return (
    <div className="space-y-6">
      <div className="border border-border bg-card rounded-xl p-5 space-y-3">
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Event title" className={input} data-testid="admin-event-title" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className={input} />
        <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className={input} data-testid="admin-event-date" />
        <input value={form.join_link} onChange={(e) => setForm({ ...form, join_link: e.target.value })} placeholder="Join link (optional)" className={input} />
        <button onClick={create} className={btn} data-testid="admin-create-event"><Plus size={14} /> Create event</button>
      </div>
      <div className="border border-border bg-card rounded-xl divide-y divide-border">
        {events.map((e) => (
          <div key={e.event_id} className="flex items-center gap-3 p-4">
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${e.cancelled ? "line-through opacity-50" : ""}`}>{e.title}</div>
              <div className="text-xs text-muted-foreground">{new Date(e.starts_at).toLocaleString()} · {e.rsvp_count} RSVPs</div>
            </div>
            <button onClick={() => cancel(e)} className={btnGhost}>{e.cancelled ? "Restore" : "Cancel"}</button>
            <button onClick={() => remove(e)} className="p-2 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Members() {
  const [members, setMembers] = useState([]);
  const load = () => api.get("/admin/members").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);
  const update = async (m, patch) => { await api.put(`/admin/members/${m.user_id}`, patch); load(); };

  return (
    <div className="border border-border bg-card rounded-xl divide-y divide-border">
      {members.map((m) => (
        <div key={m.user_id} className="flex items-center gap-3 p-4" data-testid={`admin-member-${m.user_id}`}>
          <Avatar src={m.picture} name={m.name} size={40} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium flex items-center gap-2">
              {m.name}
              {m.is_admin && <Shield size={13} className="text-gold" />}
              {m.suspended && <span className="text-[10px] font-mono uppercase text-destructive border border-destructive/40 px-1.5">suspended</span>}
            </div>
            <div className="text-xs text-muted-foreground">{m.email} · {m.points} pts · Lv{m.level.level}</div>
          </div>
          <button onClick={() => update(m, { is_admin: !m.is_admin })} className={btnGhost} data-testid={`toggle-admin-${m.user_id}`}>
            {m.is_admin ? "Revoke admin" : "Make admin"}
          </button>
          <button onClick={() => update(m, { suspended: !m.suspended })}
            className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-sm hover:bg-secondary/50 transition-colors"
            data-testid={`toggle-suspend-${m.user_id}`}>
            <Ban size={14} /> {m.suspended ? "Unsuspend" : "Suspend"}
          </button>
        </div>
      ))}
    </div>
  );
}

function Moderation() {
  const [courses, setCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");
  const load = () => {
    api.get("/admin/courses").then((r) => setCourses(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  };
  useEffect(() => { load(); }, []);

  const moderate = async (c, action) => {
    await api.put(`/admin/courses/${c.course_id}/moderate`, { action });
    toast.success(action === "approve" ? "Approved & published" : "Rejected");
    load();
  };
  const addCat = async () => {
    if (!newCat.trim()) return;
    await api.post("/admin/categories", { name: newCat.trim() });
    setNewCat(""); load(); toast.success("Category added");
  };
  const delCat = async (name) => { await api.delete(`/admin/categories/${encodeURIComponent(name)}`); load(); };

  const pending = courses.filter((c) => c.status === "pending");
  const rejected = courses.filter((c) => c.status === "rejected");

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow text-cobalt mb-3">Pending review · {pending.length}</div>
        {pending.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-xl">Nothing waiting for review.</div>
        ) : (
          <div className="space-y-3">
            {pending.map((c) => (
              <div key={c.course_id} className="border border-border bg-card rounded-xl p-4" data-testid={`pending-course-${c.course_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{c.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {c.author?.name ? `by ${c.author.name} · ` : ""}{c.lesson_count} lessons · {c.is_paid ? `$${c.price}` : "Free"}
                    </div>
                    {c.moderation_reason && <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber"><Clock size={12} /> {c.moderation_reason}</div>}
                    <div className="mt-1 flex flex-wrap gap-1.5">{c.categories?.map((x) => <span key={x} className="text-[10px] text-cobalt bg-secondary rounded-full px-2 py-0.5">{x}</span>)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => moderate(c, "approve")} data-testid={`approve-${c.course_id}`}
                      className="inline-flex items-center gap-1.5 bg-emerald text-white rounded-md px-3 py-2 text-sm hover:opacity-90 transition-opacity"><Check size={14} /> Approve</button>
                    <button onClick={() => moderate(c, "reject")} data-testid={`reject-${c.course_id}`}
                      className="inline-flex items-center gap-1.5 border border-destructive/40 text-destructive rounded-md px-3 py-2 text-sm hover:bg-destructive/10 transition-colors"><X size={14} /> Reject</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {rejected.length > 0 && (
        <div>
          <div className="eyebrow text-muted-foreground mb-3">Declined · {rejected.length}</div>
          <div className="space-y-2">
            {rejected.map((c) => (
              <div key={c.course_id} className="border border-border bg-card rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{c.title}</div>
                  <div className="text-xs text-destructive mt-0.5">{c.moderation_reason}</div>
                </div>
                <button onClick={() => moderate(c, "approve")} className={btnGhost} data-testid={`approve-${c.course_id}`}>Publish anyway</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="eyebrow text-cobalt mb-3">Course categories</div>
        <div className="border border-border bg-card rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name"
              className={input} data-testid="admin-new-category" onKeyDown={(e) => e.key === "Enter" && addCat()} />
            <button onClick={addCat} className={btn} data-testid="admin-add-category"><Plus size={14} /> Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 bg-secondary text-cobalt text-sm rounded-full pl-3 pr-1.5 py-1">
                {c}
                <button onClick={() => delCat(c)} data-testid={`del-cat-${c}`} className="hover:text-destructive"><X size={13} /></button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const RESET_PHRASE = "RESET IKIGORA";

function DangerZone() {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [backups, setBackups] = useState([]);
  const [restoring, setRestoring] = useState(null);

  const loadBackups = () => api.get("/admin/backups").then((r) => setBackups(r.data)).catch(() => {});
  useEffect(() => { loadBackups(); }, []);

  const wipes = [
    "All members (except Ikigora Staff & you)", "Posts & comments (keeps the Welcome post & Staff posts)",
    "Direct messages & connections", "Notifications", "Course progress & reviews",
    "Space subscriptions", "Events & RSVPs", "Build projects & whiteboard content",
    "Payment transaction records", "Uploaded files (keeps Staff uploads)",
  ];
  const keeps = ["Courses", "Spaces", "Categories", "Ikigora Staff account", "The pinned Welcome post", "Your admin account"];

  const reset = async () => {
    setBusy(true); setResult(null);
    try {
      const res = await api.post("/admin/reset-community-data", { confirm: phrase });
      setResult(res.data.deleted);
      setPhrase("");
      loadBackups();
      toast.success("Community data reset. A backup was saved first.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const download = async (b) => {
    try {
      const res = await api.get(`/admin/backups/${b.backup_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ikigora-backup-${b.backup_id}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const restore = async (b) => {
    if (!window.confirm(`Restore backup from ${new Date(b.created_at).toLocaleString()}? This re-adds the backed-up data.`)) return;
    setRestoring(b.backup_id);
    try {
      await api.post(`/admin/backups/${b.backup_id}/restore`);
      toast.success("Backup restored — the community data is back.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="danger-zone">
      <div className="border-2 border-destructive/40 bg-destructive/5 rounded-xl p-6 space-y-5">
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} className="text-destructive shrink-0 mt-0.5" />
          <div>
            <h2 className="font-serif text-2xl font-bold text-foreground">Reset community data</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Permanently deletes all member-generated data and returns the community to a fresh launch state.
              <strong className="text-foreground"> A full backup is saved automatically first</strong>, so a mistaken wipe can be restored.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="eyebrow text-destructive mb-2">Will be deleted</div>
            <ul className="space-y-1.5 text-muted-foreground">
              {wipes.map((w) => <li key={w} className="flex gap-2"><X size={14} className="text-destructive shrink-0 mt-0.5" /><span>{w}</span></li>)}
            </ul>
          </div>
          <div>
            <div className="eyebrow text-emerald mb-2">Will be kept</div>
            <ul className="space-y-1.5 text-muted-foreground">
              {keeps.map((k) => <li key={k} className="flex gap-2"><Check size={14} className="text-emerald shrink-0 mt-0.5" /><span>{k}</span></li>)}
            </ul>
          </div>
        </div>

        <div className="gold-rule" />
        <div>
          <label className="text-sm text-muted-foreground">Type <code className="text-foreground font-mono bg-secondary px-1.5 py-0.5 rounded">{RESET_PHRASE}</code> to confirm</label>
          <input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={RESET_PHRASE}
            data-testid="reset-confirm-input" className={`${input} mt-2`} />
        </div>
        <button onClick={reset} disabled={phrase.trim() !== RESET_PHRASE || busy} data-testid="reset-community-data-button"
          className="inline-flex items-center gap-2 bg-destructive text-white rounded-md px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
          {busy ? "Backing up & resetting…" : "Back up & reset community data"}
        </button>
      </div>

      {result && (
        <div className="border border-border bg-card rounded-xl p-5" data-testid="reset-result">
          <div className="eyebrow text-emerald mb-3 flex items-center gap-2"><Check size={14} /> Reset complete · backup saved</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-sm font-mono">
            {Object.entries(result).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>
            ))}
          </div>
          {Object.values(result).every((v) => v === 0) && <p className="text-sm text-muted-foreground">Nothing to delete — already clean.</p>}
        </div>
      )}

      <div className="border border-border bg-card rounded-xl p-5" data-testid="backups-section">
        <div className="eyebrow text-cobalt mb-1 flex items-center gap-2"><Archive size={14} /> Backups</div>
        <p className="text-xs text-muted-foreground mb-4">A snapshot is taken automatically before every reset. Download a copy for safekeeping, or restore to recover a wipe.</p>
        {backups.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">No backups yet — one is created the first time you run a reset.</div>
        ) : (
          <div className="divide-y divide-border">
            {backups.map((b) => (
              <div key={b.backup_id} className="flex items-center gap-3 py-3" data-testid={`backup-${b.backup_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{new Date(b.created_at).toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">{b.total_records} records · by {b.created_by_name}</div>
                </div>
                <button onClick={() => download(b)} data-testid={`backup-download-${b.backup_id}`} className={btnGhost}><Download size={14} /> Download</button>
                <button onClick={() => restore(b)} disabled={restoring === b.backup_id} data-testid={`backup-restore-${b.backup_id}`}
                  className="inline-flex items-center gap-1.5 border border-cobalt text-cobalt rounded-md px-3 py-2 text-sm hover:bg-cobalt hover:text-white transition-colors disabled:opacity-50">
                  {restoring === b.backup_id ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Reports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/admin/reports").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);
  const removeEvent = async (it) => {
    if (!window.confirm(`Delete "${it.event_title}"? This removes the event for everyone.`)) return;
    try { await api.delete(`/events/${it.event_id}`); toast.success("Event removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };
  const dismiss = async (it) => {
    try { await api.post(`/admin/reports/${it.event_id}/dismiss`); toast.success("Reports dismissed"); load(); }
    catch (e) { toast.error("Could not dismiss"); }
  };
  if (loading) return <div className="text-muted-foreground eyebrow py-8">Loading…</div>;
  if (items.length === 0) return (
    <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-xl" data-testid="reports-empty">
      No open reports. All clear.
    </div>
  );
  return (
    <div className="space-y-4 max-w-2xl" data-testid="reports-list">
      {items.map((it) => (
        <div key={it.event_id} className="border border-border bg-card rounded-xl p-5" data-testid={`report-${it.event_id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Flag size={15} className="text-destructive shrink-0" />
                <h3 className="font-serif text-lg truncate">{it.event_title}</h3>
                <span className="text-xs font-mono text-destructive shrink-0">{it.count} report{it.count > 1 ? "s" : ""}</span>
              </div>
              {!it.event_exists && <div className="text-xs text-muted-foreground mt-1">(event already deleted)</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {it.event_exists && (
                <button onClick={() => removeEvent(it)} data-testid={`report-remove-${it.event_id}`}
                  className="inline-flex items-center gap-1.5 bg-destructive text-white rounded-md px-3 py-1.5 text-sm hover:opacity-90 transition-opacity"><Trash2 size={14} /> Remove event</button>
              )}
              <button onClick={() => dismiss(it)} data-testid={`report-dismiss-${it.event_id}`}
                className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-sm hover:bg-secondary/50 transition-colors"><Check size={14} /> Dismiss</button>
            </div>
          </div>
          <div className="mt-3 space-y-2 border-t border-border pt-3">
            {it.reports.map((r, i) => (
              <div key={i} className="text-sm">
                <span className="text-muted-foreground">{r.reporter_name}:</span>{" "}
                {r.reason || <span className="italic text-muted-foreground">no reason given</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Steward" title="Admin" subtitle="Run the community — spaces, courses, events, and members." />
      <div className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-gold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "moderation" && <Moderation />}
      {tab === "spaces" && <Spaces />}
      {tab === "courses" && <Courses />}
      {tab === "events" && <Events />}
      {tab === "members" && <Members />}
      {tab === "reports" && <Reports />}
      {tab === "danger" && <DangerZone />}
    </div>
  );
}
